import { useEffect, useRef } from 'react';

interface SchemaOrgHeadProps {
  schema: Record<string, unknown> | Record<string, unknown>[];
  pageTitle?: string;
  pageDescription?: string;
  canonicalPath?: string;
}

/**
 * Injects a Schema.org JSON-LD script into <head> and optionally
 * updates the page <title>, <meta name="description">, and canonical link
 * via React 19 document-metadata hoisting.
 */
export default function SchemaOrgHead({
  schema,
  pageTitle,
  pageDescription,
  canonicalPath,
}: SchemaOrgHeadProps) {
  const scriptId = useRef(`schema-ld-${Math.random().toString(36).slice(2)}`).current;

  useEffect(() => {
    const existing = document.getElementById(scriptId);
    if (existing) existing.remove();

    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.id = scriptId;
    script.textContent = JSON.stringify(schema);
    document.head.appendChild(script);

    return () => {
      const el = document.getElementById(scriptId);
      if (el) el.remove();
    };
  }, [schema, scriptId]);

  const siteUrl = import.meta.env.VITE_SITE_URL ?? 'https://example.com';
  const canonical = canonicalPath ? `${siteUrl}${canonicalPath}` : undefined;

  return (
    <>
      {pageTitle && <title>{pageTitle}</title>}
      {pageDescription && <meta name="description" content={pageDescription} />}
      {canonical && <link rel="canonical" href={canonical} />}
    </>
  );
}
